/**
 * The Helm Coach — onboarding v4, the quest-FTUE's replacement (owner 2026-07-16: "aperta aqui pra mirar no
 * lado esquerdo; o medidor é a distância do tiro; você pode abaixar e levantar a mira; pressione o botão de
 * balde quando for atingido; explica sobre o vento ali no medidor — um tutorial realmente ÚTIL").
 *
 * A contextual, NON-BLOCKING tutorial that points at the REAL HUD controls while the ship stays fully
 * controllable: a small parchment card glued beside the control it teaches, a gold pulse ring on the control
 * itself and a bobbing pointing hand. Six short beats — wheel, sails, aim & fire (the elevation gauge = your
 * shot's reach), the 🪣 bucket, the wind pennant on the brass gauge, the chart — and every one advances the
 * moment the captain USES the pointed control (or taps Next). NOT a quest: there is no "go kill / loot / sell"
 * objective anywhere, only "here is the control, here is what it does".
 *
 *  · Skippable at any time ("Skip") and one-shot: done/skipped persists (`sor:tutDone`) and it never re-shows.
 *    A mid-session reload resumes at the step it was on (`sor:tutStep`); legacy veterans (the retired
 *    `sor:ftueDone` / `sor:coachDone` flags) are treated as done and never see it.
 *  · Never blocks: pointer-events pass through everywhere except the card's own two buttons; if the
 *    life-or-death 🪣 repair hint (uxhints.ts) fires mid-coach, main.ts suppresses this card so the alarm owns
 *    the screen (the coach resumes when it clears). Anchor/cargo hints hold while the coach guides — see
 *    `teachingMutes`.
 *  · Re-playable from the Book's "How to Play" chapter (ephemeral replay — a veteran quitting mid-replay is
 *    never re-armed on the next login).
 *  · Visual language: the aged-parchment kit (parchment.ts) — NO generic dark-glass. All text EN.
 *  · Telemetry (no PII): `tut_step` on each step entry, `tut_skip` (carrying the step it died on),
 *    `tut_done` on completion. The old ftue_step/ftue_done events are gone.
 *
 * The step table + advancement predicates + resume logic are PURE (unit-tested with no DOM); the class is a
 * thin DOM controller in the uxhints.ts mould (glued to the live element's rect, never a hard-coded coordinate,
 * so a HUD-Forge relocation is followed automatically). Client-only, ZERO wire.
 */
import { HELM_COACH } from '@piratas/protocol';
import { injectParchmentCss } from './parchment.js';

const LS_DONE = 'sor:tutDone';
const LS_STEP = 'sor:tutStep';
const LS_LEGACY_FTUE = 'sor:ftueDone';   // retired quest-FTUE flag — read as "already onboarded", never written
const LS_LEGACY_COACH = 'sor:coachDone'; // pre-FTUE coach flag — same
export const COACH_DONE_KEY = '__done__';

/** Live per-frame signals the sea loop feeds in (all state main.ts already holds — zero new reads). */
export interface CoachFrame {
  alive: boolean;
  rudder: number;      // −1..1 live steering input
  sailTarget: number;  // 0..1 sail throttle the captain has set
  speed: number;       // sim m/s
  bailing: boolean;    // the 🪣 control is held this frame
  dtMs: number;        // ms since the previous frame (drives the hold accumulators)
}

/** Accumulators for the hold-to-advance steps (kept outside the step table so the table stays const). */
export interface CoachAcc { steerMs: number; bailMs: number; }

export interface CoachStep {
  key: string;
  /** HUD element ids to ring — [0] is the one the hand points at. */
  targets: string[];
  icon: string;
  title: string;
  body: string;
  /** Label for the advance button ('use' steps keep it as an escape hatch — nobody is ever stuck). */
  nextLabel: string;
  /** PURE frame predicate for the steps that advance on real use (undefined = a notify hook / Next only). */
  done?: (f: CoachFrame, acc: CoachAcc) => boolean;
}

/** The six beats, in HUD order. Copy is command-focused ("press here, this is what the gauge means") — never
 *  an errand. A broadside into empty waves counts; bailing a dry hull counts: the ACTION is the lesson. */
export const COACH_STEPS: readonly CoachStep[] = [
  {
    key: 'steer', targets: ['steer'], icon: '☸️', title: 'Take the Wheel',
    body: 'Drag the wheel left or right to lay the rudder over — she answers as she sails.',
    nextLabel: 'Next ›',
    done: (f, acc) => { if (f.alive && Math.abs(f.rudder) >= HELM_COACH.STEER_MIN_RUDDER) acc.steerMs += f.dtMs; return acc.steerMs >= HELM_COACH.STEER_HOLD_MS; },
  },
  {
    key: 'sails', targets: ['sail', 'anchor'], icon: '⛵', title: 'Make Sail',
    body: 'Tap ⛵ to set your sail and watch the knots climb. If she won\'t budge, weigh the ⚓ anchor.',
    nextLabel: 'Next ›',
    done: (f) => f.alive && (f.sailTarget >= HELM_COACH.SAIL_MIN_TARGET || f.speed >= HELM_COACH.SAIL_MIN_SPEED),
  },
  {
    key: 'aim', targets: ['fireR', 'fireL', 'elevwrap'], icon: '💥', title: 'Aim & Fire',
    body: 'HOLD a side to run out those guns — the gauge is your shot\'s reach. Slide up or down to raise the aim, let go to FIRE. Rounds are finite: restock at any port.',
    nextLabel: 'Next ›',
    // advances on notifyFire (a server-confirmed broadside — empty waves count, no target needed)
  },
  {
    key: 'bail', targets: ['bail'], icon: '🪣', title: 'Man the Bucket',
    body: 'When shots land, the sea pours in — HOLD 🪣 to bail her dry before she goes down. Try a few pumps now.',
    nextLabel: 'Next ›',
    done: (f, acc) => { if (f.alive && f.bailing) acc.bailMs += f.dtMs; return acc.bailMs >= HELM_COACH.BAIL_HOLD_MS; },
  },
  {
    key: 'wind', targets: ['speedo'], icon: '🌬', title: 'Mind the Wind',
    body: 'The brass gauge reads your knots — its pennant shows the wind. Pennant at the top = wind astern = full speed; beat against it and she crawls.',
    nextLabel: 'Got it ›',
    // nothing to press — Next only
  },
  {
    key: 'chart', targets: ['mapwrap'], icon: '🗺', title: 'Chart a Course',
    body: 'Tap the chart and the quartermaster steers there for you. Gold, cargo & hold ride the top bar — and the Book 📖 holds everything else.',
    nextLabel: 'Finish ⚓',
    // advances on notifyWaypoint (a plotted course) — or Finish
  },
] as const;
export const COACH_DONE_STEP = COACH_STEPS.length;

/** PURE: is the coach armed for this device? Never for a veteran of ANY earlier onboarding (retired FTUE /
 *  retired coach) — those flags are read-only here and left inert so migrated profiles are never re-tutored. */
export function coachArmed(tutDone: boolean, legacyOnboarded: boolean): boolean {
  return !tutDone && !legacyOnboarded;
}

/** PURE resume: a saved step KEY → the index to resume at (order-independent; unknown/absent → start over,
 *  the done sentinel → done). */
export function coachResume(savedKey: string | null | undefined): number {
  if (!savedKey) return 0;
  if (savedKey === COACH_DONE_KEY) return COACH_DONE_STEP;
  const i = COACH_STEPS.findIndex((s) => s.key === savedKey);
  return i >= 0 ? i : 0;
}

export interface CoachDeps {
  sfx?: { ui: () => void; step: () => void; done: () => void };
  tele?: {
    step?: (key: string) => void;  // entered a step (once per key per session)
    skip?: (key: string) => void;  // skipped — carries the step it died on
    done?: () => void;             // completed the final step
  };
  clock?: () => number;
}

export class Coach {
  /** Armed = this device still owes the tutorial (drives the auto-start AND main's "brand-new captain" read). */
  readonly armed: boolean;
  private step: number;
  private finished: boolean;
  private atSea = false;
  private started = false;         // the START_DELAY_MS fuse has been lit this session
  private live = false;            // a card is currently mounted
  private suppressed = false;      // an overlay / the repair alarm owns the screen — card hidden, state ticking
  private ephemeral = false;       // menu replay — never persists
  private acc: CoachAcc = { steerMs: 0, bailMs: 0 };
  private announced = new Set<string>();
  private card: HTMLElement | null = null;
  private hand: HTMLElement | null = null;
  private ringed: HTMLElement[] = [];
  private startTimer: number | null = null;

  constructor(private readonly host: HTMLElement, private readonly deps: CoachDeps = {}) {
    let done = false, legacy = false, savedKey: string | null = null;
    try {
      done = localStorage.getItem(LS_DONE) === '1';
      legacy = !!(localStorage.getItem(LS_LEGACY_FTUE) || localStorage.getItem(LS_LEGACY_COACH));
      savedKey = localStorage.getItem(LS_STEP);
    } catch { /* private mode → treat as new (worst case: the coach shows once per session) */ }
    this.armed = coachArmed(done, legacy);
    this.step = this.armed ? coachResume(savedKey) : COACH_DONE_STEP;
    this.finished = !this.armed || this.step >= COACH_DONE_STEP;
  }

  /** True while the coach still has teaching to do this session (mutes anchor/cargo hints + the Opportunities
   *  board, exactly the slot the FTUE used to own). */
  guiding(): boolean { return !this.finished; }

  /** Reached the open sea — light the start fuse (once per session) or re-show the card after a port visit. */
  onSeaEntry(): void {
    this.atSea = true;
    if (this.finished) return;
    if (this.live) { this.render(); return; }
    if (this.started) { this.show(); return; }
    this.started = true;
    this.startTimer = window.setTimeout(() => {
      this.startTimer = null;
      if (this.atSea && !this.finished) this.show();
    }, HELM_COACH.START_DELAY_MS);
  }

  /** Stepped ashore / into the house — hide the card (sea controls left the screen), keep the state. */
  onLeaveSea(): void {
    this.atSea = false;
    this.hideCard();
  }

  /** An overlay (the Primer manual, the Book) or the life-or-death 🪣 alarm owns the screen: hide the card
   *  but keep ticking, so a lesson learned during the alarm still advances the step. */
  setSuppressed(on: boolean): void {
    if (on === this.suppressed) return;
    this.suppressed = on;
    if (on) this.hideCard();
    else if (this.atSea && !this.finished && this.started) this.show();
  }

  /** Server-confirmed broadside — completes the aim step (a shot into empty waves counts). */
  notifyFire(): void { if (this.currentKey() === 'aim') this.advance(); }

  /** A course was plotted on the chart — completes the chart step. */
  notifyWaypoint(): void { if (this.currentKey() === 'chart') this.advance(); }

  /** Each sea frame: run the current step's use-predicate + keep the card glued to its (relocatable) control. */
  tick(f: CoachFrame): void {
    if (this.finished) return;
    // tick only ever runs from the SEA loop, so a tick while not marked at-sea means the session STARTED on
    // open water (a returning spawn skips the port-transition path that fires onSeaEntry) — self-arm here,
    // exactly like the old tutorial recovered on direct sea spawns.
    if (!this.atSea) { this.onSeaEntry(); return; }
    const s = COACH_STEPS[this.step];
    if (s?.done && s.done(f, this.acc)) { this.advance(); return; }
    if (this.live) this.reposition();
  }

  /** Menu replay for anyone (Book → How to Play). Ephemeral: nothing persists, a quit mid-replay never re-arms. */
  restart(): void {
    this.ephemeral = true;
    this.finished = false;
    this.started = true;
    this.step = 0;
    this.acc = { steerMs: 0, bailMs: 0 };
    if (this.atSea && !this.suppressed) this.show();
  }

  /** Read-only introspection for tests + the headless smoke hook. */
  debug(): { step: number; key: string; live: boolean; guiding: boolean; armed: boolean } {
    return { step: this.step, key: this.currentKey(), live: this.live, guiding: this.guiding(), armed: this.armed };
  }

  /* ------------------------------------ progression ------------------------------------ */

  private currentKey(): string {
    return this.finished || this.step >= COACH_DONE_STEP ? COACH_DONE_KEY : COACH_STEPS[this.step]!.key;
  }

  private advance(): void {
    if (this.finished) return;
    this.deps.sfx?.step();
    this.step += 1;
    this.acc = { steerMs: 0, bailMs: 0 };
    if (this.step >= COACH_DONE_STEP) { this.finale(); return; }
    this.persist();
    if (this.atSea && !this.suppressed) this.show();
  }

  private skip(): void {
    this.deps.tele?.skip?.(this.currentKey());
    this.deps.sfx?.ui();
    this.finish();
  }

  private finale(): void {
    this.deps.tele?.done?.();
    this.deps.sfx?.done();
    // Teaching is OVER the moment the last lesson lands — `guiding()` flips false right here so the hints /
    // Opportunities board unmute immediately; only the little "fair winds" flourish card lingers below.
    this.finished = true;
    this.persistDone();
    if (this.atSea && !this.suppressed) {
      this.ensureCard();
      this.setRings([]);
      this.placeHand(null);
      this.card!.querySelector('.tc-body')!.innerHTML =
        `<div class="tc-row"><span class="tc-ic">🏴‍☠️</span><div class="tc-tx"><b class="pg-title tc-t">Fair winds, Captain</b><span>She's all yours — the sea decides the rest.</span></div></div>`;
      this.card!.style.left = '50%';
      this.card!.style.transform = 'translateX(-50%)';
      this.card!.style.top = 'calc(env(safe-area-inset-top, 8px) + 200px)';
      this.card!.style.bottom = 'auto';
      this.card!.classList.add('on', 'tc-final');
      this.live = true;
      window.setTimeout(() => this.finish(), 2800);
    } else {
      this.finish();
    }
  }

  /** Retire for good (done or skipped) — every hook then short-circuits on `finished`. */
  private finish(): void {
    this.finished = true;
    this.step = COACH_DONE_STEP;
    if (this.startTimer != null) { clearTimeout(this.startTimer); this.startTimer = null; }
    this.persistDone();
    this.hideCard();
  }

  private persist(): void {
    if (this.ephemeral) return;
    try { localStorage.setItem(LS_STEP, this.currentKey()); } catch { /* private mode */ }
  }

  private persistDone(): void {
    if (this.ephemeral) return;
    try { localStorage.setItem(LS_DONE, '1'); localStorage.setItem(LS_STEP, COACH_DONE_KEY); } catch { /* private mode */ }
  }

  /* --------------------------------------- view --------------------------------------- */

  private show(): void {
    if (this.finished || this.step >= COACH_DONE_STEP) return;
    const s = COACH_STEPS[this.step]!;
    if (!this.announced.has(s.key)) { this.announced.add(s.key); this.deps.tele?.step?.(s.key); }
    this.render();
  }

  private render(): void {
    const s = COACH_STEPS[this.step]; if (!s) return;
    this.ensureCard();
    const dots = COACH_STEPS.map((_, i) => `<i class="tc-dot${i < this.step ? ' past' : i === this.step ? ' on' : ''}"></i>`).join('');
    this.card!.querySelector('.tc-body')!.innerHTML =
      `<div class="tc-row"><span class="tc-ic">${s.icon}</span><div class="tc-tx"><b class="pg-title tc-t">${s.title}</b><span>${s.body}</span></div></div>` +
      `<div class="tc-foot"><button class="tc-skip" type="button">Skip</button><span class="tc-dots">${dots}</span><button class="tc-next" type="button">${s.nextLabel}</button></div>`;
    this.card!.querySelector('.tc-skip')!.addEventListener('pointerdown', (e) => { e.preventDefault(); this.skip(); });
    this.card!.querySelector('.tc-next')!.addEventListener('pointerdown', (e) => { e.preventDefault(); this.deps.sfx?.ui(); this.advance(); });
    this.card!.classList.remove('tc-final');
    this.card!.classList.add('on');
    this.live = true;
    this.setRings(s.targets);
    this.reposition();
  }

  private hideCard(): void {
    this.setRings([]);
    this.placeHand(null);
    if (this.card) { const c = this.card; c.classList.remove('on'); this.card = null; window.setTimeout(() => c.remove(), 320); }
    if (this.hand) { const h = this.hand; this.hand = null; h.remove(); }
    this.live = false;
  }

  /** Gold pulse ring on the real controls this step teaches (by stable id — survives HUD Forge moves). */
  private setRings(ids: string[]): void {
    for (const e of this.ringed) e.classList.remove('tc-target');
    this.ringed = [];
    for (const id of ids) {
      const e = document.getElementById(id);
      if (e) { e.classList.add('tc-target'); this.ringed.push(e); }
    }
  }

  /** Glue the card + the bobbing hand beside the step's primary control, clamped on-screen (uxhints mould). */
  private reposition(): void {
    if (!this.card) return;
    const s = COACH_STEPS[this.step]; if (!s) return;
    const target = this.ringed[0] ?? document.getElementById(s.targets[0]!);
    if (!target) { // control not on screen (edge layouts) — park the card top-centre, no hand
      this.card.style.left = '50%'; this.card.style.transform = 'translateX(-50%)';
      this.card.style.top = 'calc(env(safe-area-inset-top, 8px) + 200px)'; this.card.style.bottom = 'auto';
      this.placeHand(null);
      return;
    }
    const r = target.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // not laid out yet
    const vw = window.innerWidth || 390, vh = window.innerHeight || 844;
    const cw = this.card.offsetWidth || 250, chh = this.card.offsetHeight || 120;
    this.card.style.transform = 'none';
    let left = r.left + r.width / 2 - cw / 2;
    left = Math.max(8, Math.min(left, vw - cw - 8));
    this.card.style.left = `${Math.round(left)}px`;
    const fitsAbove = r.top - chh - 40 >= 8;
    if (fitsAbove) {
      this.card.style.top = 'auto';
      this.card.style.bottom = `${Math.round(vh - r.top + 34)}px`;
      this.card.classList.remove('below');
      this.placeHand(r, 'above');
    } else {
      this.card.style.bottom = 'auto';
      this.card.style.top = `${Math.round(r.bottom + 34)}px`;
      this.card.classList.add('below');
      this.placeHand(r, 'below');
    }
  }

  /** The pointing hand bobs in the gap between card and control — 👇 when the card sits above it, 👆 below. */
  private placeHand(r: DOMRect | null, side: 'above' | 'below' = 'above'): void {
    if (!r) { if (this.hand) this.hand.style.display = 'none'; return; }
    if (!this.hand) {
      const h = document.createElement('div');
      h.id = 'tc-hand';
      this.host.appendChild(h);
      this.hand = h;
    }
    this.hand.style.display = 'block';
    this.hand.textContent = side === 'above' ? '👇' : '👆';
    this.hand.className = side;
    this.hand.style.left = `${Math.round(r.left + r.width / 2 - 14)}px`;
    if (side === 'above') { this.hand.style.top = 'auto'; this.hand.style.bottom = `${Math.round((window.innerHeight || 844) - r.top + 2)}px`; }
    else { this.hand.style.bottom = 'auto'; this.hand.style.top = `${Math.round(r.bottom + 2)}px`; }
  }

  private ensureCard(): void {
    if (this.card) return;
    injectParchmentCss();
    if (!document.getElementById('tc-style')) {
      const st = document.createElement('style'); st.id = 'tc-style';
      st.textContent = COACH_CSS;
      document.head.appendChild(st);
    }
    const el = document.createElement('div');
    el.id = 'helmcoach';
    el.innerHTML = '<div class="tc-body pg-card"></div>';
    this.host.appendChild(el);
    this.card = el;
  }
}

/* Parchment coach card — a chart-card (the shared .pg-card paper + ink neat-line + brass studs) glued beside
 * the live control, with a gold pulse ring on the control itself and a bobbing pointing hand. Everything is
 * pointer-events:none except the two small buttons, so the game underneath stays fully playable. */
const COACH_CSS = `
#helmcoach{ position:fixed; z-index:46; left:0; top:120px; width:min(78vw,262px); pointer-events:none;
  opacity:0; transform:translateY(6px); transition:opacity .3s ease, transform .3s ease; font-family:inherit; }
#helmcoach.on{ opacity:1; transform:translateY(0); }
#helmcoach .tc-body{ padding:11px 13px 9px; border-radius:9px; }
#helmcoach .tc-row{ display:flex; align-items:flex-start; gap:10px; }
#helmcoach .tc-ic{ font-size:23px; line-height:1.1; filter:drop-shadow(0 1px 1px rgba(60,38,10,.4)); }
#helmcoach .tc-tx{ display:flex; flex-direction:column; gap:3px; min-width:0; }
#helmcoach .tc-t{ font-size:13px; }
#helmcoach .tc-tx span{ font-size:12px; font-weight:600; line-height:1.45; color:#3b2712;
  font-family:'Iowan Old Style','Palatino Linotype','Book Antiqua',Palatino,Georgia,serif; }
#helmcoach .tc-foot{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:8px; }
#helmcoach .tc-dots{ display:flex; gap:5px; }
#helmcoach .tc-dot{ width:8px; height:8px; border-radius:50%;
  background:radial-gradient(circle at 36% 32%, #caa27a, #8a6a4a 65%, #5c4630); opacity:.45;
  box-shadow:inset 0 -1px 1px rgba(40,24,6,.4); transition:all .25s ease; }
#helmcoach .tc-dot.past{ opacity:.9;
  background:radial-gradient(circle at 36% 32%, #9fbf7a, #5f7a3a 65%, #3a4f22); }
#helmcoach .tc-dot.on{ opacity:1; transform:scale(1.3);
  background:radial-gradient(circle at 36% 32%, #d96a52, #a3282e 60%, #5e0f14);
  box-shadow:inset 0 -1px 2px rgba(40,4,4,.5), 0 0 6px rgba(163,40,46,.5); }
#helmcoach .tc-next{ pointer-events:auto; min-height:44px; min-width:76px; padding:6px 13px; border-radius:9px; cursor:pointer;
  font-family:'Cinzel',Georgia,serif; font-weight:800; font-size:11px; letter-spacing:.08em; text-transform:uppercase;
  color:#2a1c06; background:linear-gradient(180deg,#e9c869,#c99a3a 55%,#a97b22);
  border:1px solid #7a5416; box-shadow:inset 0 1px 0 rgba(255,246,214,.6), 0 1px 3px rgba(40,24,6,.3); }
#helmcoach .tc-next:active{ transform:translateY(1px); }
#helmcoach .tc-skip{ pointer-events:auto; min-height:44px; padding:6px 8px; border:0; background:none; cursor:pointer;
  font:inherit; font-size:10.5px; font-weight:800; letter-spacing:.5px; text-transform:uppercase;
  color:#8a5c14; opacity:.8; text-decoration:underline; text-underline-offset:2px; }
#helmcoach.tc-final .tc-body{ box-shadow:inset 0 0 0 1px rgba(58,40,16,.5), inset 0 0 0 3px rgba(58,40,16,.14),
  inset 0 0 26px rgba(70,44,18,.14), 0 0 22px -4px rgba(232,192,116,.7), 0 2px 6px rgba(40,24,6,.14); }
/* the bobbing pointing hand, in the gap between card and control */
#tc-hand{ position:fixed; z-index:47; font-size:26px; pointer-events:none; filter:drop-shadow(0 2px 3px rgba(0,0,0,.5)); }
#tc-hand.above{ animation:tcBobDown 1.1s ease-in-out infinite; }
#tc-hand.below{ animation:tcBobUp 1.1s ease-in-out infinite; }
@keyframes tcBobDown{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(6px); } }
@keyframes tcBobUp{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-6px); } }
/* the gold pulse ring on the REAL control — id-level so it reads over the HUD; taps pass through untouched */
#hud .tc-target, #topbar .tc-target{ animation:tcPulse 1.2s ease-out infinite; }
@keyframes tcPulse{ 0%{ box-shadow:0 0 0 0 rgba(255,214,120,0), 0 0 0 2.5px rgba(255,214,120,.95); }
  70%{ box-shadow:0 0 0 13px rgba(255,214,120,0), 0 0 0 2.5px rgba(255,214,120,.55); }
  100%{ box-shadow:0 0 0 0 rgba(255,214,120,0), 0 0 0 2.5px rgba(255,214,120,0); } }
`;
